import { SignalDoc } from "@megaron/docs-contracts";

import { SignalsSortValue } from "./filterSignals";
import { getSignalDisplayName } from "./getSignalDisplayName";
import { signalLevelRank } from "./signalLevel";

export type SignalDocumentGroup = {
  key: string;
  name: string;
  app: string;
  signals: SignalDoc[];
};

export const signalDocumentKey = (signal: SignalDoc): string =>
  `${signal.subjectDocType}:${signal.subjectDocId}`;

export const isSignalGroupUnread = (group: SignalDocumentGroup): boolean =>
  group.signals.some((signal) => !signal.isRead);

export const groupSignals = (signals: SignalDoc[]): SignalDocumentGroup[] => {
  const byKey = signals.reduce<Map<string, SignalDoc[]>>((groups, signal) => {
    const key = signalDocumentKey(signal);
    const group = groups.get(key) ?? [];
    groups.set(key, [...group, signal]);
    return groups;
  }, new Map());

  return [...byKey.entries()].map(([key, groupSignals]) => {
    const sortedSignals = [...groupSignals].sort(
      (left, right) => right.createdAt.getTime() - left.createdAt.getTime(),
    );
    const primary = sortedSignals[0];
    return {
      key,
      name: getSignalDisplayName(primary),
      app: primary.app,
      signals: sortedSignals,
    };
  });
};

const compareGroups = (
  left: SignalDocumentGroup,
  right: SignalDocumentGroup,
  sort: SignalsSortValue,
  appLabel: (app: string) => string,
): number => {
  const direction = sort.order === "ASC" ? 1 : -1;
  const leftPrimary = left.signals[0];
  const rightPrimary = right.signals[0];

  if (sort.field === "createdAt") {
    return (leftPrimary.createdAt.getTime() - rightPrimary.createdAt.getTime()) * direction;
  }

  if (sort.field === "name") {
    return left.name.localeCompare(right.name, "pl") * direction;
  }

  if (sort.field === "app") {
    return appLabel(left.app).localeCompare(appLabel(right.app), "pl") * direction;
  }

  const leftLevel = Math.max(...left.signals.map((signal) => signalLevelRank[signal.level]));
  const rightLevel = Math.max(...right.signals.map((signal) => signalLevelRank[signal.level]));
  return (leftLevel - rightLevel) * direction;
};

export const sortSignalGroups = (
  groups: SignalDocumentGroup[],
  sort: SignalsSortValue | undefined,
  appLabel: (app: string) => string,
): SignalDocumentGroup[] => {
  if (!sort) return groups;
  return [...groups].sort((left, right) => compareGroups(left, right, sort, appLabel));
};
